// A/B test: does showing "Drop a Hint" actually move revenue? Each visitor is randomly assigned to
// "shown" or "hidden" once, remembered across visits (localStorage), and that assignment is reported
// to GA4 tagged with the same client_id the store's existing GA4 tracking already uses — so it can be
// joined against real revenue/conversion data in GA4's own reporting. See
// /scratchpad/drop-a-hint-worker/worker.js's /track route for where that report actually goes (the API
// secret can't live here in public theme JS, so this just hands off to our own Worker).

const VARIANT_STORAGE_KEY = 'dah_experiment_variant';
const TRACK_ENDPOINT = 'https://drop-a-hint-attentive-proxy.drop-a-hint-worker.workers.dev/track';

function getOrAssignVariant() {
  let variant;

  try {
    variant = window.localStorage.getItem(VARIANT_STORAGE_KEY);
  } catch (error) {
    // Storage unavailable (private browsing, locked-down browser settings) — assign per page load.
  }

  if (variant !== 'shown' && variant !== 'hidden') {
    variant = Math.random() < 0.5 ? 'shown' : 'hidden';

    try {
      window.localStorage.setItem(VARIANT_STORAGE_KEY, variant);
    } catch (error) {
      // Fine if this fails — the visitor just gets reassigned next page load instead of persisting.
    }
  }

  return variant;
}

function getGa4ClientId() {
  const match = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);

  if (!match) {
    return null;
  }

  const parts = match[1].split('.');

  if (parts.length < 4) {
    return null;
  }

  return `${parts[2]}.${parts[3]}`;
}

function trackExposure(variant) {
  const clientId = getGa4ClientId();

  if (!clientId) {
    return;
  }

  // Fire-and-forget — this is analytics, it should never block or error the page.
  fetch(TRACK_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ client_id: clientId, variant }),
    keepalive: true,
  }).catch(() => {});
}

document.addEventListener('DOMContentLoaded', () => {
  // There can be more than one trigger on a page (e.g. the main product info block and a sticky
  // add-to-cart bar both render their own copy) — every copy needs to move together, or a visitor
  // could see the button in one place but not the other.
  const triggers = document.querySelectorAll('.drop-a-hint-trigger');

  if (!triggers.length) {
    return;
  }

  // Always show in the theme editor's Customize view — Shopify sets this flag specifically for that
  // context, not for a regular storefront visit or a preview link opened in a normal tab. Merchants
  // editing the theme should see everything that's live, not get randomly bucketed like a visitor.
  if (window.Shopify && window.Shopify.designMode) {
    triggers.forEach((trigger) => {
      trigger.hidden = false;
    });
    return;
  }

  const variant = getOrAssignVariant();

  if (variant === 'shown') {
    triggers.forEach((trigger) => {
      trigger.hidden = false;
    });
  }

  // Report once per page per session — avoids inflating exposure counts on repeat views.
  const sessionKey = `dah_experiment_tracked_${window.location.pathname}`;

  try {
    if (!window.sessionStorage.getItem(sessionKey)) {
      trackExposure(variant);
      window.sessionStorage.setItem(sessionKey, '1');
    }
  } catch (error) {
    trackExposure(variant);
  }
});
